/**
 * `synkro grade <edit|bash>` — replacement for the legacy
 * ~/.synkro/bin/grader_daemon.py. Reads a grading payload from stdin,
 * routes it through the local-CC channel, and writes the raw verdict
 * (including the <synkro-verdict>...</synkro-verdict> wrapper that the
 * shell hook scripts grep for) to stdout.
 *
 * Failures print to stderr and exit non-zero so the hook scripts fall
 * through to the cloud fast-tier path cleanly.
 */

import { submitToChannel, LocalCCError } from '../local-cc/client.js';
import type { LocalCCRole } from '../local-cc/prompts.js';

async function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    process.stdin.on('data', c => chunks.push(c));
    process.stdin.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
    process.stdin.on('error', reject);
  });
}

export async function gradeCommand(args: string[]): Promise<void> {
  const mode = args[0] ?? '';
  let role: LocalCCRole;
  if (mode === 'edit') role = 'grade-edit';
  else if (mode === 'bash') role = 'grade-bash';
  else if (mode === 'plan') role = 'grade-plan';
  else if (mode === 'cwe') role = 'grade-cwe';
  else {
    console.error('Usage: synkro grade <edit|bash|plan|cwe>');
    process.exit(2);
  }

  const payload = await readStdin();
  if (!payload.trim()) {
    console.error('synkro grade: empty stdin');
    process.exit(2);
  }

  try {
    const result = await submitToChannel(role, payload, { timeoutMs: 60_000 });
    process.stdout.write(result);
    if (!result.endsWith('\n')) process.stdout.write('\n');
  } catch (err) {
    if (err instanceof LocalCCError) {
      console.error(`synkro grade: ${err.message}`);
    } else {
      console.error(`synkro grade: ${(err as Error).message}`);
    }
    process.exit(3);
  }
}
